export function getProductCategoryName(product) {
  const category = product?.category;
  if (!category) return null;
  if (typeof category === "string") return category;
  return category.name || category.label || null;
}

const normalizeCategory = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export function categoriesAlign(intentCategory, productCategory) {
  const left = normalizeCategory(intentCategory);
  const right = normalizeCategory(productCategory);
  if (!left || !right) return true;
  if (left === right || left.includes(right) || right.includes(left)) return true;

  const leftTokens = new Set(left.split(" ").filter(Boolean));
  return right
    .split(" ")
    .filter(Boolean)
    .some((token) => leftTokens.has(token));
}

export function intentConflictsWithProducts(searchParams, products = []) {
  if (!searchParams || !products.length) return false;

  const intentCategory = searchParams?.category;
  if (intentCategory) {
    const productCategories = products
      .map(getProductCategoryName)
      .filter(Boolean);
    if (
      productCategories.length &&
      !productCategories.some((name) => categoriesAlign(intentCategory, name))
    ) {
      return true;
    }
  }

  const productFamily = getProductCategoryFamily(products);
  const intentText = serializeIntentSignals(searchParams);
  if (!productFamily || !intentText) return false;

  if (productFamily === "phone" && TV_INTENT_PATTERN.test(intentText)) {
    return true;
  }
  if (productFamily === "tv" && PHONE_INTENT_PATTERN.test(intentText)) {
    return true;
  }

  return false;
}

const TV_INTENT_PATTERN =
  /\b(television|televisions|\btv\b|home theater|movie|movies|hdr|4k|oled)\b/i;
const PHONE_INTENT_PATTERN =
  /\b(smartphone|smartphones|mobile phone|cell phone|\bphone\b|selfie|portable)\b/i;

function serializeIntentSignals(searchParams) {
  return [
    searchParams.category,
    searchParams.useCase,
    ...(searchParams.mustHaves || []),
    ...(searchParams.niceToHaves || []),
  ]
    .filter(Boolean)
    .join(" ");
}

function getProductCategoryFamily(products = []) {
  const combined = products
    .map(getProductCategoryName)
    .filter(Boolean)
    .map(normalizeCategory)
    .join(" ");

  if (/smartphones?|\bmobile\b|cell phone|\bphones?\b/.test(combined)) {
    return "phone";
  }
  if (/\b(tvs?|televisions?|display|monitor)\b/.test(combined)) return "tv";
  return null;
}

function getMatchProfileCategory(matchProfile) {
  const category = matchProfile?.category;
  if (!category) return null;
  if (typeof category === "string") return category;
  return category.name || category.label || null;
}

export function matchProfileConflictsWithProducts(matchProfile, products = []) {
  const profileCategory = getMatchProfileCategory(matchProfile);
  if (!profileCategory || !products.length) return false;

  const productCategories = products
    .map(getProductCategoryName)
    .filter(Boolean);
  if (!productCategories.length) return false;

  return !productCategories.some((name) =>
    categoriesAlign(profileCategory, name),
  );
}

function deriveSearchParamsFromProducts(products = []) {
  const categories = [
    ...new Set(products.map(getProductCategoryName).filter(Boolean)),
  ];
  if (categories.length !== 1) return null;
  return { category: categories[0] };
}

function stripConflictingIntentFields(searchParams, products) {
  if (!searchParams || !intentConflictsWithProducts(searchParams, products)) {
    return searchParams;
  }
  return deriveSearchParamsFromProducts(products);
}

/** Align chat/search intent with the products actually being compared. */
export function resolveSearchParamsForProducts(products = [], searchParams = null) {
  return stripConflictingIntentFields(searchParams, products);
}

/**
 * Build a safe AI session payload for comparison summaries.
 * Prefers explicit searchParams from the current compare flow over stale cache.
 */
export function resolveComparisonAiSession({
  products = [],
  aiSession = null,
  searchParams = null,
} = {}) {
  const comparisonProductIds = new Set(
    products.map((product) => String(product._id || product.id)).filter(Boolean),
  );

  let resolvedSearchParams = searchParams || null;
  let resolvedMatchProfile = null;

  if (!resolvedSearchParams && aiSession?.products?.length && comparisonProductIds.size) {
    const sessionProductIds = new Set(
      aiSession.products
        .map((product) => String(product._id || product.id))
        .filter(Boolean),
    );
    const productsInSession = [...comparisonProductIds].every((id) =>
      sessionProductIds.has(id),
    );

    if (productsInSession) {
      resolvedSearchParams = aiSession.searchParams || null;
      resolvedMatchProfile = aiSession.matchProfile || null;
    }
  }

  resolvedSearchParams = stripConflictingIntentFields(
    resolvedSearchParams,
    products,
  );

  if (
    resolvedMatchProfile &&
    matchProfileConflictsWithProducts(resolvedMatchProfile, products)
  ) {
    resolvedMatchProfile = null;
  }

  if (!resolvedSearchParams && !resolvedMatchProfile) {
    resolvedSearchParams = deriveSearchParamsFromProducts(products);
  }

  if (!resolvedSearchParams && !resolvedMatchProfile) {
    return null;
  }

  return {
    ...(aiSession || {}),
    searchParams: resolvedSearchParams,
    matchProfile: resolvedMatchProfile,
  };
}
